<template>
    <div v-if="isOpen" class="modal-fullscreen">
        <div class="modal-header">
            <div class="modal-title-container">
                <h3 v-if="modalType === 'lesson'">{{ currentLesson ? `Aula ${String(currentLesson.ordem_aula).padStart(2, '0')}` : '' }}</h3>
                <h3 v-else-if="modalType === 'simulado'">Simulado</h3>
            </div>
            <div v-if="modalType === 'lesson'" class="modal-header-tabs">
                <button 
                    v-if="currentLesson && currentLesson.url_video_aula"
                    :class="['modal-tab-btn', { active: activeModalTab === 'video' }]"
                    @click="$emit('set-tab', 'video')"
                >
                    <i class="fas fa-play-circle"></i> Vídeo Aula
                </button>
                <button 
                    v-if="currentLesson && currentLesson.hash_arq_pdf_aula"
                    :class="['modal-tab-btn', { active: activeModalTab === 'pdf' }]"
                    @click="$emit('set-tab', 'pdf')"
                >
                    <i class="fas fa-file-pdf"></i> Material Didático
                </button>
                <button 
                    v-if="questoesSimulado && questoesSimulado.length > 0"
                    :class="['modal-tab-btn', { active: activeModalTab === 'simulado' }]"
                    @click="$emit('set-tab', 'simulado')"
                >
                    <i class="fas fa-clipboard-check"></i> Simulado
                </button>
            </div>
            <button class="btn-close" @click="$emit('close')"><i class="fas fa-times"></i> Fechar</button>
        </div>
        
        <div class="modal-body">
            <div v-if="loadingLesson" class="modal-loading-state">
                <div class="spinner"></div>
                <p>Carregando conteúdo...</p>
            </div>

            <template v-else-if="modalType === 'lesson' && activeModalTab === 'video'">
                <iframe ref="vimeoIframe" v-if="currentMediaVideo" :src="currentMediaVideo" width="100%" height="100%" frameborder="0" allow="autoplay; fullscreen" allowfullscreen></iframe>
                <div v-else class="modal-no-content">Nenhum vídeo disponível para esta aula.</div>
            </template>

            <template v-else-if="modalType === 'lesson' && activeModalTab === 'pdf'">
                <div v-if="loadingPdf" class="content-loader">
                    <div class="spinner"></div>
                    <p style="color: white; margin-top: 10px;">Carregando material didático...</p>
                </div>
                <iframe v-else-if="currentMediaPdf" :src="currentMediaPdf" width="100%" height="100%" frameborder="0"></iframe>
                <div v-else class="modal-no-content">Nenhum material didático em PDF disponível para esta aula.</div>
            </template>
            
            <div v-else-if="modalType === 'simulado' || (modalType === 'lesson' && activeModalTab === 'simulado')" class="simulado-container">
                <div v-for="(q, index) in questoesSimulado" :key="q.codigo_questao" class="questao-box">
                    <h3>{{ index + 1 }}. {{ q.enunciado_questao }}</h3>
                    <ul class="opcoes-lista">
                        <li v-for="opt in ['opcao1', 'opcao2', 'opcao3', 'opcao4', 'opcao5']" :key="opt" 
                            :class="['opcao-item', { 
                                selecionada: respostasSimulado[q.codigo_questao] === opt,
                                correta: simuladoEnviado && q.opcao_correta && String(q.opcao_correta).includes(opt.replace('opcao', '')),
                                errada: simuladoEnviado && respostasSimulado[q.codigo_questao] === opt && (!q.opcao_correta || !String(q.opcao_correta).includes(opt.replace('opcao', '')))
                            }]"
                            @click="!simuladoEnviado && selecionarOpcao(q.codigo_questao, opt)">
                            <div class="radio-circle"><i v-if="respostasSimulado[q.codigo_questao] === opt" class="fas fa-circle"></i></div>
                            <span>{{ q['questao_' + opt] }}</span>
                        </li>
                    </ul>
                    <div v-if="simuladoEnviado" class="gabarito-feedback">
                        <i class="fas fa-info-circle"></i> Gabarito oficial: Alternativa {{ q.opcao_correta }}
                    </div>
                </div>

                <div class="simulado-actions" v-if="questoesSimulado.length > 0">
                    <button v-if="!simuladoEnviado" class="modern-btn btn-enviar" @click="enviarSimulado">
                        <i class="fas fa-paper-plane"></i> Enviar Simulado
                    </button>
                    <div v-else class="sucesso-msg-container" style="display: flex; flex-direction: column; gap: 15px; align-items: flex-end;">
                        <div class="sucesso-msg">
                            <i class="fas fa-check-circle"></i> Simulado concluído!
                            <div class="nota-resultado">
                                <span class="nota-valor">Nota: {{ notaSimulado }}</span>
                                <span class="nota-detalhe">{{ acertosSimulado }} de {{ questoesSimulado.length }} acertos</span>
                            </div>
                        </div>
                        <button class="btn-refazer-modal" @click="iniciarRefazer">
                            <i class="fas fa-redo"></i> Refazer Simulado
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'EmbedLessonModal',
    props: {
        isOpen: { type: Boolean, required: true },
        modalType: { type: String, required: true },
        activeModalTab: { type: String, required: true },
        currentLesson: { type: Object, default: null },
        currentMediaVideo: { type: String, default: null },
        currentMediaPdf: { type: String, default: null },
        loadingPdf: { type: Boolean, default: false },
        loadingLesson: { type: Boolean, default: false },
        questoesSimulado: { type: Array, default: () => [] },
        savedAnswers: { type: Object, default: () => ({}) }
    },
    data() {
        return {
            player: null,
            respostasSimulado: {},
            videoCompleted: false,
            simuladoEnviado: false,
            notaSimulado: 0,
            acertosSimulado: 0,
            videoWatched: false,
            pdfViewed: false,
            simuladoCompleted: false,
            lessonMarkedComplete: false
        };
    },
    watch: {
        isOpen(val) {
            if (!val && this.player) {
                this.player.destroy();
                this.player = null;
            }
            if (val) {
                this.respostasSimulado = {};
                this.simuladoEnviado = false;
                this.notaSimulado = 0;
                this.acertosSimulado = 0;
                this.videoWatched = false;
                this.pdfViewed = false;
                this.simuladoCompleted = false;
                this.lessonMarkedComplete = false;
                this.restoreSavedAnswers();
            }
            if (val && this.modalType === 'lesson' && this.activeModalTab === 'video') {
                this.initVimeoPlayer();
            }
        },
        activeModalTab(val) {
            if (val === 'pdf') {
                this.markPdfViewed();
            }
            if (val === 'video') {
                this.initVimeoPlayer();
            }
        },
        currentMediaVideo() {
            this.videoCompleted = false;
            this.videoWatched = false;
            if (this.isOpen && this.activeModalTab === 'video') {
                this.initVimeoPlayer();
            }
        },
        loadingLesson(newVal, oldVal) {
            if (oldVal === true && newVal === false) {
                this.restoreSavedAnswers();
            }
        }
    },
    methods: {
        async loadVimeoApi() {
            if (window.Vimeo && window.Vimeo.Player) return true;
            return new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = 'https://player.vimeo.com/api/player.js';
                script.onload = () => resolve(true);
                script.onerror = reject;
                document.head.appendChild(script);
            });
        },
        async initVimeoPlayer() {
            if (this.player) {
                this.player.destroy();
                this.player = null;
            }
            try {
                await this.loadVimeoApi();
                this.$nextTick(() => {
                    if (this.$refs.vimeoIframe && window.Vimeo) {
                        this.player = new window.Vimeo.Player(this.$refs.vimeoIframe);
                        
                        this.player.on('timeupdate', (data) => {
                            if (!this.videoCompleted && data.percent >= 0.7) {
                                this.videoCompleted = true;
                                this.videoWatched = true;
                                this.checkLessonCompletion();
                            }
                        });
                    }
                });
            } catch(e) {
                console.error("Erro ao carregar Vimeo API:", e);
            }
        },
        markPdfViewed() {
            if (this.currentLesson && !this.pdfViewed) {
                this.pdfViewed = true;
                this.checkLessonCompletion();
            }
        },
        selecionarOpcao(codigoQuestao, opcao) {
            this.$set(this.respostasSimulado, codigoQuestao, opcao);
        },
        enviarSimulado() {
            const questoesCount = this.questoesSimulado.length;
            const answeredCount = Object.keys(this.respostasSimulado).length;

            if (answeredCount < questoesCount) {
                alert("Por favor, responda todas as questões antes de enviar.");
                return;
            }

            let acertos = 0;
            const answersPayload = [];
            this.questoesSimulado.forEach(q => {
                const respostaAluno = this.respostasSimulado[q.codigo_questao];
                let isCorrect = false;
                if (respostaAluno && q.opcao_correta) {
                    const numeroResposta = respostaAluno.replace('opcao', '');
                    if (String(q.opcao_correta).includes(numeroResposta)) {
                        acertos++;
                        isCorrect = true;
                    }
                }
                answersPayload.push({
                    question_code: String(q.codigo_questao),
                    selected_option: respostaAluno,
                    is_correct: isCorrect
                });
            });
            const nota = questoesCount > 0 ? parseFloat(((acertos / questoesCount) * 10).toFixed(1)) : 0;
            this.notaSimulado = nota;
            this.acertosSimulado = acertos;

            this.simuladoEnviado = true;
            this.simuladoCompleted = true;

            const lessonCode = this.currentLesson ? this.currentLesson.codigo_aula : null;

            if (lessonCode) {
                this.$emit('save-answers', {
                    lesson_code: lessonCode,
                    answers: answersPayload,
                    score: nota
                });
            }

            this.checkLessonCompletion();
        },
        restoreSavedAnswers() {
            if (!this.savedAnswers || Object.keys(this.savedAnswers).length === 0) return;
            if (!this.questoesSimulado || this.questoesSimulado.length === 0) return;

            const respostas = {};
            let acertos = 0;

            this.questoesSimulado.forEach(q => {
                const saved = this.savedAnswers[String(q.codigo_questao)];
                if (saved) {
                    this.$set(respostas, q.codigo_questao, saved.selected_option);
                    if (saved.is_correct) acertos++;
                }
            });

            if (Object.keys(respostas).length > 0) {
                this.respostasSimulado = respostas;
                this.simuladoEnviado = true;
                this.simuladoCompleted = true;
                this.acertosSimulado = acertos;
                this.notaSimulado = parseFloat(((acertos / this.questoesSimulado.length) * 10).toFixed(1));
            }
        },
        checkLessonCompletion() {
            if (!this.currentLesson || this.lessonMarkedComplete) return;

            const hasVideo = !!this.currentLesson.url_video_aula;
            const hasPdf = !!this.currentLesson.hash_arq_pdf_aula;
            const hasSimulado = this.questoesSimulado && this.questoesSimulado.length > 0;

            if (hasVideo && !this.videoWatched) return;
            if (hasPdf && !this.pdfViewed) return;
            if (hasSimulado && !this.simuladoCompleted) return;

            this.lessonMarkedComplete = true;
            this.$emit('progress-update', {
                type: 'lesson',
                id: this.currentLesson.codigo_aula,
                score: this.simuladoCompleted ? this.notaSimulado : null
            });
        },
        iniciarRefazer() {
            this.respostasSimulado = {};
            this.simuladoEnviado = false;
            this.simuladoCompleted = false;
            this.notaSimulado = 0;
            this.acertosSimulado = 0;
            this.lessonMarkedComplete = false;
        }
    }
}
</script>

<style scoped src="./EmbedLessonModal.css" />
